import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface BonusCardProps {
  emoji: string;
  title: string;
  description: string;
  index: number;
}
function BonusCard({
  emoji,
  title,
  description,
  index
}: BonusCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} className="relative p-8 rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 shadow-lg" initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.15
  }} whileHover={{
    scale: 1.02
  }} data-id="element-12">
      <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-amber-400 to-amber-500 rounded-full flex items-center justify-center text-white text-xl shadow-lg" data-id="element-13">
        ⭐
      </div>
      <div className="text-5xl mb-4" data-id="element-14">{emoji}</div>
      <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-15">{title}</h3>
      <p className="text-slate-600 leading-relaxed" data-id="element-16">{description}</p>
    </motion.div>;
}
export function BonusSection() {
  const bonuses = [{
    emoji: '💐',
    title: 'Día de la Madre',
    description: 'Diseños especiales para la fecha más emotiva del año. Tarjetas, decoraciones y detalles únicos.'
  }, {
    emoji: '🏫',
    title: 'Etiquetas de Colegio',
    description: 'Pack completo de etiquetas para útiles, ropa y libros. ¡Un bestseller garantizado cada temporada!'
  }, {
    emoji: '🔤',
    title: 'Letras 3D',
    description: 'Letras y números en 3D para decoraciones impactantes. Ideales para fotos, fiestas y eventos especiales.'
  }];
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, {
    once: true
  });
  const manualRef = useRef(null);
  const isManualInView = useInView(manualRef, {
    once: true
  });
  return <section className="w-full bg-gradient-to-b from-white to-pink-50 py-20 sm:py-28" data-id="element-17">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18">
        {/* Header */}
        <motion.div ref={headerRef} className="text-center mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={isHeaderInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-19">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" data-id="element-20">
            ⭐ Bonos Especiales
          </h2>
          <p className="text-xl sm:text-2xl font-bold text-amber-500 mb-4" data-id="element-21">
            Regalos que Multiplican el Valor de tu Kit 🎀
          </p>
          <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-22">
            Además de las 26 carpetas principales, recibes estos bonos
            exclusivos sin costo adicional.
          </p>
        </motion.div>

        {/* Bonus Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12" data-id="element-23">
          {bonuses.map((bonus, index) => <BonusCard key={bonus.title} emoji={bonus.emoji} title={bonus.title} description={bonus.description} index={index} data-id="element-24" />)}
        </div>

        {/* Manual Card */}
        <motion.div ref={manualRef} className="relative p-8 sm:p-10 rounded-3xl bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-xl overflow-hidden" initial={{
        opacity: 0,
        y: 40
      }} animate={isManualInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} data-id="element-25">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-26" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" data-id="element-27" />

          <div className="relative flex flex-col sm:flex-row items-center gap-6" data-id="element-28">
            <div className="text-6xl sm:text-7xl" data-id="element-29">📖</div>
            <div className="text-center sm:text-left" data-id="element-30">
              <h3 className="text-2xl sm:text-3xl font-bold mb-3" data-id="element-31">
                Manual Completo Incluido
              </h3>
              <p className="text-rose-100 text-lg leading-relaxed" data-id="element-32">
                Kit Imprimible Profesional para Fiestas — Guía integral de
                materiales, papeles, impresión, armado y recomendaciones
                técnicas.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Price reminder */}
        <motion.div className="mt-12 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-33">
          <p className="text-slate-600 mb-3" data-id="element-34">
            26 carpetas + 5 bonos + manual completo
          </p>
          <div className="inline-flex items-center gap-3" data-id="element-35">
            <span className="text-slate-400 line-through text-lg" data-id="element-36">$89.990</span>
            <span className="text-3xl font-extrabold text-rose-600" data-id="element-37">
              $27.990 ARS
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-500" data-id="element-38">
            Pago único · Descarga inmediata
          </p>
        </motion.div>
      </div>
    </section>;
}